import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { clydeflowAPI, propertiesAPI, eventsAPI } from '../../api';
import { Rocket, Loader2, Trash2, ImageOff, Home, Calendar, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const imgSrc = (url) => (url && url.startsWith('/') ? `${API}${url}` : url);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

/**
 * ClydeFlowDraftQueue — one-screen bulk review: every ClydeFlow draft listed
 * with a checkbox (ticked by default) → "Publish N selected" in one click.
 * Per-row bin to discard junk. Hidden entirely when there are no drafts.
 */
const ClydeFlowDraftQueue = ({ reloadToken, onPublished }) => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]); // [{type, ...doc}]
  const [selected, setSelected] = useState(new Set()); // keys `${type}:${id}`
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await clydeflowAPI.drafts();
      const merged = [
        ...(r.data.properties || []).map((p) => ({ ...p, type: 'properties' })),
        ...(r.data.events || []).map((e) => ({ ...e, type: 'events' })),
      ];
      setRows(merged);
      setSelected(new Set(merged.map((x) => `${x.type}:${x.id}`)));
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadToken]);

  if (rows.length === 0) return null;

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const items = rows
        .filter((x) => selected.has(`${x.type}:${x.id}`))
        .map((x) => ({ type: x.type, id: x.id }));
      const r = await clydeflowAPI.publishDrafts({ items });
      toast({
        title: `${r.data.total} published`,
        description: `${r.data.published_properties} properties (live for 10 days) · ${r.data.published_events} events.`,
      });
      load();
      onPublished?.();
    } catch (e) {
      toast({ title: 'Publish failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setPublishing(false);
    }
  };

  const discard = async (row) => {
    if (!window.confirm(`Discard draft "${row.title}"?`)) return;
    try {
      if (row.type === 'properties') await propertiesAPI.delete(row.id);
      else await eventsAPI.delete(row.id);
      toast({ title: 'Draft discarded' });
      load();
    } catch (e) {
      toast({ title: 'Discard failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="bg-white border-2 border-sky-200 shadow-sm rounded-xl overflow-hidden" data-testid="clydeflow-draft-queue">
      <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-sky-50/50">
        <div>
          <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Rocket size={15} className="text-sky-500" /> Drafts awaiting publish
          </p>
          <p className="text-xs text-slate-500 mt-0.5">{rows.length} imported item{rows.length === 1 ? '' : 's'} — untick anything you don't want, then publish the rest in one go.</p>
        </div>
        <Button
          onClick={publish}
          disabled={publishing || selected.size === 0}
          className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg"
          data-testid="draft-queue-publish-btn"
        >
          {publishing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle2 size={14} className="mr-2" />}
          {publishing ? 'Publishing…' : `Publish ${selected.size} selected`}
        </Button>
      </div>

      <div className="max-h-[380px] overflow-y-auto divide-y divide-slate-100">
        {rows.map((row) => {
          const key = `${row.type}:${row.id}`;
          const details = row.type === 'properties'
            ? [row.address, row.bedrooms ? `${row.bedrooms} bed` : '', row.property_type,
               row.monthly_rent ? `£${row.monthly_rent} pcm` : ''].filter(Boolean).join(' · ')
            : [row.venue, fmtDate(row.start_date)].filter(Boolean).join(' · ');
          return (
            <div key={key} className="px-5 py-2.5 flex items-center gap-3 hover:bg-slate-50/60" data-testid={`draft-row-${row.id}`}>
              <input
                type="checkbox"
                className="w-4 h-4 rounded accent-emerald-500 shrink-0"
                checked={selected.has(key)}
                onChange={() => toggle(key)}
                data-testid={`draft-check-${row.id}`}
              />
              {row.image_url ? (
                <img src={imgSrc(row.image_url)} alt="" className="w-12 h-9 object-cover rounded-md border border-slate-200 shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-12 h-9 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
                  <ImageOff size={12} className="text-slate-300" />
                </div>
              )}
              <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                row.type === 'properties' ? 'bg-emerald-50 text-emerald-700' : 'bg-violet-50 text-violet-700'
              }`}>
                {row.type === 'properties' ? <Home size={9} /> : <Calendar size={9} />}
                {row.type === 'properties' ? 'Property' : 'Event'}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">{row.title}</p>
                <p className="text-[11px] text-slate-500 truncate">{details}</p>
              </div>
              <button
                type="button"
                onClick={() => discard(row)}
                aria-label={`Discard ${row.title}`}
                className="p-1.5 text-slate-300 hover:text-red-500 transition-colors shrink-0"
                data-testid={`draft-discard-${row.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClydeFlowDraftQueue;
